import { createSelector } from 'reselect';
import {
    calculateCartTotal, calculateProductTotal, cartAccountSelector,
    cartCartSelector,
    cartIncludedProductsSelector,
    cartPeriodIdFromQuerySelector,
} from './cartSelector';
import { pagesTariffsByProductSelector } from './pagesSelectors';
import { productCurrentProductIdSelector } from './productSelector';
import { getLogger } from '../../lib/devMode';
import { MINIMUM_ALLOWED_ORDER_TOTAL, MINIMUM_ALLOWED_PRODUCT_PRICE } from '../../constants/constants';
import isSelectorIndifferentProduct from '../../resolvers/isSelectorIndifferentProduct';
import getCurrentTariffByProduct from '../../resolvers/getCurrentTariffByProduct';
import getDiscountAmountByProduct from '../../resolvers/getDiscountAmountByProduct';
import isAllowedTariffForUser from '../../resolvers/isAllowedTariffForUser';
import { default as _u } from 'underscore';
import getProductWithAppliedAccount from '../../mutators/getProductWithAppliedAccount';
import getBillingUnitsCount from '../../resolvers/getBillingUnitsCount';

export const filtersPeriodsByProductSelector = (state, { product_id }) => {
    return state?.filters?.periods[product_id];
};

export const filtersPeriodsSelector = (state) => state.filters.periods;
export const filtersCurrenciesSelector = (state) => state.filters.currencies;
export const filtersTariffsByPeriodSelector = createSelector(
    [pagesTariffsByProductSelector, cartCartSelector, (state, props) => props],
    (tariffs, cart, { product_id }) => {
        if (!!cart?.products[product_id]?.period) {
            return Object.values(tariffs)
                ?.filter(
                    (tariff) =>
                        tariff?.billing_period ===
                        cart?.products[product_id]?.period?.billing_period
                )
                .reduce((acc, tariff) => {
                    acc[tariff.tariff_id] = { ...tariff };

                    return acc;
                }, {});
        }

        return tariffs;
    }
);

export const filterPeriodSelector = createSelector(
    [
        filtersPeriodsByProductSelector,
        cartCartSelector,
        pagesTariffsByProductSelector,
        cartIncludedProductsSelector,
        productCurrentProductIdSelector,
        (state) => state,
        (state, props) => props,
    ],
    (periods, cart, tariffs, includedProducts, mainProductId, state, { product_id }) => {
        const product = cart.products[product_id];
        const currentTariffId = getCurrentTariffByProduct(product);
        if (!!currentTariffId) {
            return Object.values(periods)
                ?.map((period) => {
                    const isDisabledPeriod = {
                        ...period,
                        isDisabled: false,
                    };

                    if (
                        includedProducts?.filter(
                            (product) => +product?.product_id !== mainProductId
                        )?.length
                    ) {
                        Object.values(includedProducts)?.map((product) => {
                            const featuredTariffs = pagesTariffsByProductSelector(state, {
                                product_id: product?.product_id,
                            });
                            Object.values(featuredTariffs)
                                ?.sort((a, b) => a?.cost - b?.cost)
                                ?.filter(
                                    (tariff) => tariff?.billing_period === period?.billing_period
                                )
                                ?.forEach((tariff) => {
                                    isDisabledPeriod.isDisabled =
                                        tariff?.cost * period?.interval - getDiscountAmountByProduct(product) <
                                        MINIMUM_ALLOWED_PRODUCT_PRICE;
                                });
                        });
                    } else {
                        Object.values(tariffs)
                            ?.sort((a, b) => a?.cost - b?.cost)
                            ?.filter((tariff) => tariff?.billing_period === period?.billing_period)
                            ?.forEach((tariff) => {
                                isDisabledPeriod.isDisabled =
                                    tariff?.cost * period?.interval - getDiscountAmountByProduct(product) < MINIMUM_ALLOWED_PRODUCT_PRICE;
                            });
                    }

                    return {
                        ...isDisabledPeriod,
                    };
                })
                .reduce((acc, period) => {
                    acc[period.name] = period;

                    return acc;
                }, {});
        }

        return periods;
    }
);

export const filterPeriodsByQuery = createSelector(
    [filterPeriodSelector, cartPeriodIdFromQuerySelector],
    (periods, periodFromQuery) => {
        if (periods && periods[periodFromQuery]) {
            return periods[periodFromQuery];
        }

        return {};
    }
);

export const newFiltersTariffsSelector = createSelector(
    [
        pagesTariffsByProductSelector,
        cartCartSelector,
        cartIncludedProductsSelector,
        productCurrentProductIdSelector,
        cartAccountSelector,
        (state) => state,
        (state, props) => props,
    ],
    (tariffsByProduct, cart, includedProducts, mainProductId, account,state, props) => {
        const product = Object.values(cart?.products)
            ?.filter((product) => +product?.product_id === +props?.product_id)
            ?.shift();
        return Object.values(tariffsByProduct)
            ?.sort((a, b) => +a?.services - +b?.services)
            ?.reduce((acc, tariff) => {
                const isDisabledTariff = {
                    ...tariff,
                    isDisabled: false,
                };
                const productInCart = cart?.products[mainProductId] ?? product;

                if (
                    includedProducts?.filter((product) => +product?.product_id !== mainProductId)
                        ?.length
                ) {
                    const billingUnitsInTariff = getBillingUnitsCount({
                        tariff,
                        product_id: mainProductId
                    }, cart);
                    isDisabledTariff.isDisabled = (
                        productInCart?.hasOwnProperty('services_count')
                        && (productInCart?.services_count > billingUnitsInTariff && billingUnitsInTariff !== 0)
                    ) || isDisabledTariffMethod({
                        mainProductId,
                        products: includedProducts,
                        tariff,
                        state,
                    });
                    if (!isDisabledTariff.isDisabled) {
                        getLogger().debug('[FILTER > TARIFFS]: START')
                        getLogger().debug({tariff, isDisabledTariff});
                        getLogger().debug('[FILTER > TARIFFS]: END')
                    }
                } else {
                    isDisabledTariff.isDisabled = !Object.values(tariff?.periods)?.find(
                        (period) => {
                            const cartProducts = Object.values(cart?.products)
                                ?.filter(item => item?.product_id !== product?.product_id)
                                ?.map(item => {
                                    return { ...item, period };
                                });
                            const totalCart = calculateCartTotal({
                                ...cart,
                                products: _u.object(cartProducts?.map(item => item?.product_id), cartProducts)
                            });
                            const totalProduct = calculateProductTotal({
                                ...product,
                                tariff,
                                period
                            }, cart, true);
                            const total = (totalCart?.total ?? 0) + totalProduct;

                            if ((total < MINIMUM_ALLOWED_ORDER_TOTAL || totalProduct < MINIMUM_ALLOWED_PRODUCT_PRICE)
                                || (product?.services_count > tariff.services && tariff.services !== 0)
                                || (product?.product_id === 7
                                    && (product?.accounts?.find(item => +item?.account_id === +account)?.storage < tariff?.storage)
                                    && (tariff?.projects && tariff?.projects > 0 && product?.account_projects > tariff?.projects))
                            ) {
                                return false;
                            }
                            return true;
                        },
                        true
                    );
                }

                acc[tariff?.tariff_id] = { ...isDisabledTariff };
                return acc;
            }, {});
    }
);

export const newFiltersPeriodByTariffSelector = createSelector(
    [
        filtersPeriodsByProductSelector,
        cartCartSelector,
        cartIncludedProductsSelector,
        productCurrentProductIdSelector,
        (state) => state,
        (state, props) => props,
    ],
    (periodsByProduct, cart, includedProducts, mainProductId, state, { product_id }) => {
        // Corner-case - SSL cannot be a cross-selling product for any other product.
        if (mainProductId !== 5) {
            includedProducts = includedProducts?.filter(item => item?.product_id !== 5);
        }
        const tariff = cart?.products[product_id]?.tariff;
        const product = cart?.products[product_id];

        if (!periodsByProduct && mainProductId) {
            periodsByProduct = filtersPeriodsByProductSelector(state, {product_id: mainProductId});
        }

        getLogger().debug({
            debug: 'Period selector > Before filtration',
            periodsByProduct
        });

        if (!!tariff) {
            return Object.values(periodsByProduct)
                ?.filter((period) => period?.billing_period === tariff?.billing_period)
                ?.reduce((acc, period) => {
                    const isDisabledPeriod = {
                        ...period,
                        isDisabled: false,
                    };

                    if (
                        includedProducts?.filter(
                            (product) => product?.product_id !== mainProductId
                        )?.length
                    ) {
                        isDisabledPeriod.isDisabled = isDisabledPeriodMethod({
                            products: includedProducts,
                            cart,
                            state,
                            tariff,
                            period,
                        });
                        getLogger().debug({
                            debug: 'Period selector > Filter by cross-products check',
                            product,
                            isDisabledPeriod,
                            includedProducts,
                            state,
                            tariff,
                            period
                        });
                    } else {
                        const cartProducts = Object.values(cart?.products)
                            ?.filter(item => item?.product_id !== product?.product_id && item?.product_id !== 5)
                            ?.map(item => getProductWithAppliedAccount({ ...item, period }, cart?.account))
                        const totalCart = calculateCartTotal({
                            ...cart,
                            products: _u.object(cartProducts?.map(item => item?.product_id), cartProducts)
                        });
                        const totalProduct = calculateProductTotal({ ...product, tariff, period }, cart, true);
                        const total = (totalCart?.total ?? 0) + totalProduct;
                        // Intentionally NL, to preserve 1st level git log on the next line.
                        isDisabledPeriod.isDisabled =
                            total < MINIMUM_ALLOWED_ORDER_TOTAL || totalProduct < MINIMUM_ALLOWED_PRODUCT_PRICE;
                        getLogger().debug({
                            debug: 'Period selector > Filter by tariff',
                            product,
                            isDisabledPeriod,
                            includedProducts,
                            state,
                            tariff,
                            period
                        });
                    }
                    acc[isDisabledPeriod?.name] = { ...isDisabledPeriod };
                    return acc;
                }, {});
        }

        return periodsByProduct ?? {};
    }
);

const isDisabledPeriodMethod = ({ products, state, tariff, period, cart }) => {
    return (
        Object.values(products)?.reduce((acc, product) => {
            if (isSelectorIndifferentProduct(product)) {
                return false;
            }
            const featuredTariffs = pagesTariffsByProductSelector(state, {
                product_id: product?.product_id,
            });
            const featuredPeriods = filtersPeriodsByProductSelector(state, {
                product_id: product?.product_id,
            });
            const featuredTariff = Object.values(featuredTariffs)
                ?.filter(
                    (featuredTariff) =>
                        +featuredTariff?.services === +tariff?.services &&
                        featuredTariff?.billing_period === tariff?.billing_period
                )
                ?.shift();

            const cartProducts = Object.values(cart?.products)
                ?.filter(item => item?.product_id !== product?.product_id)
                ?.map(item => {
                    return { ...item, period: featuredPeriods[period?.name] ?? period };
                });
            const totalCart = calculateCartTotal({
                ...cart,
                products: _u.object(cartProducts?.map(item => item?.product_id), cartProducts)
            });
            const totalProduct = calculateProductTotal({ ...product, tariff: featuredTariff }, cart, true);
            const total = (totalCart?.total ?? 0) + totalProduct;
            acc = total < MINIMUM_ALLOWED_PRODUCT_PRICE || totalProduct < MINIMUM_ALLOWED_PRODUCT_PRICE;

            return acc;
        }, false) ||
        Object.values(products)?.reduce((acc, product) => {
            if (isSelectorIndifferentProduct(product)) {
                return false;
            }

            const featuredPeriods = filtersPeriodsByProductSelector(state, {
                product_id: product?.product_id,
            });

            acc = !featuredPeriods[period?.name];

            return acc;
        }, false)
    );
};

const isDisabledTariffMethod = ({ mainProductId, products, state, tariff }) => {
    const cart = cartCartSelector(state);
    return (
        Object.values(products)?.reduce((acc, product) => {
            const featuredTariffs = pagesTariffsByProductSelector(state, {
                product_id: product?.product_id,
            });
            const featuredTariff = Object.values(featuredTariffs)
                ?.filter(
                    (featuredTariff) =>
                        getBillingUnitsCount({ tariff: featuredTariff, product_id: product?.product_id }, cart)
                            === getBillingUnitsCount({ tariff: tariff, product_id: product?.product_id }, cart) &&
                        featuredTariff?.billing_period === tariff?.billing_period &&
                        isAllowedTariffForUser({ tariff: featuredTariff, product })
                )
                ?.shift();
            acc = featuredTariff?.periods
                ? Object.values(featuredTariff?.periods)?.reduce((acc, period) => {
                        const cartProducts = Object.values(cart?.products)
                            ?.filter(item => item?.product_id !== product?.product_id)
                            ?.map(item => {
                                return { ...item, period };
                            });
                        const totalCart = calculateCartTotal({
                            ...cart,
                            products: _u.object(cartProducts?.map(item => item?.product_id), cartProducts)
                        });
                        const totalProduct = calculateProductTotal({
                            ...product,
                            tariff: featuredTariff,
                            period
                        }, cart, true);
                        const total = (totalCart?.total ?? 0) + totalProduct;
                        acc = total < MINIMUM_ALLOWED_ORDER_TOTAL || totalProduct < MINIMUM_ALLOWED_PRODUCT_PRICE;

                      return acc;
                  }, false)
                : false;
            return acc;
        }, false) ||
        Object.values(products)?.reduce((acc, product) => {
            const tariffsByProduct = pagesTariffsByProductSelector(state, {
                product_id: product?.product_id,
            });

            acc = product?.product_id === mainProductId ? !Object.values(tariffsByProduct)?.filter(
                (tariffByProduct) => getBillingUnitsCount({ tariff: tariffByProduct, product: product?.product_id }, cart)
                    === getBillingUnitsCount({ tariff: tariff, product: product?.product_id }, cart)
            )?.length : !Object.values(tariffsByProduct)?.filter(
                (tariffByProduct) => getBillingUnitsCount({ tariff: tariffByProduct, product: product?.product_id }, cart)
                    >= getBillingUnitsCount({ tariff: tariff, product: product?.product_id }, cart)
            )?.length

            return acc;
        }, false)
    );
};
